import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Archive, Plug, Server, type LucideIcon } from 'lucide-react';
import { Button, Drawer } from '@/components/ui';
import { AgentSetup } from '@/components/nodes/AgentSetup';
import { ConnectHostForm } from '@/components/nodes/ConnectHostForm';
import { ConnectNodeForm } from '@/components/nodes/ConnectNodeForm';
import type { EnrollmentIssued } from '@/types/api';

type Kind = 'node' | 'host';

/** What a kind's form tells the drawer, whose footer holds its submit button. */
export interface ConnectFormProps {
  readonly formId: string;
  readonly onIssued: (issued: EnrollmentIssued) => void;
  readonly onStatus: (status: { readonly ready: boolean; readonly busy: boolean }) => void;
}

const KINDS: readonly { id: Kind; icon: LucideIcon; title: string; text: string }[] = [
  {
    id: 'node',
    icon: Server,
    title: 'Сервер для сайтов панели',
    text: 'Чистый сервер из 2d2s для размещения сайтов WordPress.',
  },
  {
    id: 'host',
    icon: Archive,
    title: 'Внешний сервер с сайтами',
    text: 'Сервер с работающими сайтами: резервные копии и мониторинг.',
  },
];

const FORM_ID = 'connect-server';

/**
 * Connecting a server, whichever kind (TZ section 10.5, ADR-0033): the
 * operator says what the server is for, fills in that kind's form, and then
 * puts the agent on it (ADR-0036). The token appears only on that last screen.
 */
export function ConnectDrawer({ open, onClose }: { readonly open: boolean; readonly onClose: () => void }): ReactNode {
  const [kind, setKind] = useState<Kind | null>(null);
  const [issued, setIssued] = useState<EnrollmentIssued | null>(null);
  const [status, setStatus] = useState({ ready: false, busy: false });
  const onStatus = useCallback((next: { readonly ready: boolean; readonly busy: boolean }) => setStatus(next), []);

  // Every opening starts clean: the last token must not be on screen for the next server.
  useEffect(() => {
    if (!open) return;
    setKind(null);
    setIssued(null);
    setStatus({ ready: false, busy: false });
  }, [open]);

  if (issued) {
    return (
      <Drawer
        open={open}
        title={`Сервер ${issued.nodeName} добавлен`}
        description="Установите агента"
        onClose={onClose}
        width="lg"
        footer={
          <Button variant="primary" onClick={onClose}>
            Готово
          </Button>
        }
      >
        <AgentSetup issued={issued} />
      </Drawer>
    );
  }

  return (
    <Drawer
      open={open}
      title="Подключить сервер"
      description="Выберите тип сервера"
      onClose={onClose}
      width="md"
      footer={
        <>
          <Button onClick={onClose} disabled={status.busy}>
            Отмена
          </Button>
          <Button type="submit" form={FORM_ID} variant="primary" icon={Plug} busy={status.busy} disabled={kind === null || !status.ready}>
            Подключить
          </Button>
        </>
      }
    >
      <div className="stack">
        <div className="kind-choice" role="radiogroup" aria-label="Что это за сервер">
          {KINDS.map(({ id, icon: Icon, title, text }) => (
            <label key={id} className={`choice${kind === id ? ' choice-active' : ''}`}>
              <input type="radio" name="server-kind" value={id} checked={kind === id} disabled={status.busy} onChange={() => setKind(id)} />
              <span className="stack stack-tight">
                <span className="row-tight strong">
                  <Icon size={16} aria-hidden="true" />
                  {title}
                </span>
                <span className="text-sm muted">{text}</span>
              </span>
            </label>
          ))}
        </div>
        {kind === 'node' ? (
          <ConnectNodeForm formId={FORM_ID} onIssued={setIssued} onStatus={onStatus} />
        ) : kind === 'host' ? (
          <ConnectHostForm formId={FORM_ID} onIssued={setIssued} onStatus={onStatus} />
        ) : null}
      </div>
    </Drawer>
  );
}
